import React, { useState, useRef } from 'react';
import { Spin, Tooltip } from 'antd';
import styles from './../index.module.css';
import TaskCount from '../TaskCount';
import WebsiteMenu from './WebsiteMenu';
import { formatDistanceStrict, parseISO } from 'date-fns';
import EmptyProjectSite from './../assets/empty_project_site.png';
import { useDesignAssetsState } from '../../../../clients/providers/DesignAssets';
import AddWebsiteModal from './AddWebsiteModal';
import * as translations from '../strings';
import { getLangKey } from 'appJS/models/Application';
import ProxyCompatibilityChecker from './../ProxyCompatibilityChecker';
import ShareButton from 'appJS/components/ShareButton';
import { SiteLabels } from './SiteLabels';
import cx from 'classnames';
import { Website } from '../types';

const strings = translations[getLangKey()];

export const cleanUrl = (url: string) =>
  url.replace(/^(https?:\/\/)?(.+?)\/?$/i, '$2');

type Props = {
  site: Website;
  index: number;
  project: any;
  apiKey: string;
  proxyHost: string;
  container: any;
  showProxyPreview: boolean;
  loadingCoverUrl: boolean;
  getCoverImage: any;
  onSharingClick: () => void;
  allLabels: string[];
  projectSiteLabelsAllowed: boolean;
  show: boolean;
};

const ProjectSite = ({
  site,
  index,
  project,
  apiKey,
  proxyHost,
  container,
  showProxyPreview,
  loadingCoverUrl,
  getCoverImage,
  onSharingClick,
  allLabels,
  projectSiteLabelsAllowed,
  show
}: Props) => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const { isGuest } = useDesignAssetsState();

  const handleOpenModal = () => setOpenEditModal(prev => !prev);
  const handleCloseWebsiteModal = () => setOpenEditModal(false);

  const proxyUrl = `https://${project.apiKey}_${site.id}.${proxyHost}`;

  const showButtonOrText = () =>
    loadingCoverUrl ? (
      <Spin />
    ) : (
      <img
        width={130}
        height={100}
        src={EmptyProjectSite}
        alt="Your project site"
      />
    );

  const { id, url, cover_url: coverUrl, updated_at: updatedAt } = site;

  const childRef = useRef<{ handleClick: () => void }>(null);

  const handleCardClick = () => {
    if (openEditModal) return;
    if (childRef.current) {
      // @ts-expect-error
      childRef.current.handleTriggerClick();
    }
  };

  const onShareButtonClick = e => {
    e.preventDefault();
    e.stopPropagation();
    onSharingClick();
  };

  return (
    <div
      // @ts-expect-error
      style={{ '--index': index }}
      key={id}
      data-index={index}
      className={cx(
        styles.websiteCard,
        styles.staggerInAnimation,
        'projectSiteCard',
        { [styles.hiddenSite]: !show }
      )}
      onClick={handleCardClick}
    >
      {site.labels && (
        <>
          <div className={styles.siteLabels}>
            {site.labels?.slice(0, 3).map(label => (
              <div className={styles.projectSiteLabel}>{label}</div>
            ))}
            {site.labels?.length > 3 && (
              <Tooltip
                title={site.labels.slice(3).join(', ')}
                placement="top"
                className={styles.projectSiteLabelTooltip}
                getPopupContainer={() => container}
              >
                <div className={styles.projectSiteLabel}>
                  +{site.labels.length - 3}
                </div>
              </Tooltip>
            )}
          </div>
          <div className={styles.siteLabels} title={site.labels.join(', ')} />
        </>
      )}
      <div className={styles.loadingWebsiteImage}>
        {coverUrl ? <img src={coverUrl} /> : showButtonOrText()}
      </div>
      <div className={styles.cardContent}>
        <div className={styles.leftCardContent}>
          <h3 title={url}>{cleanUrl(url)}</h3>
          <div className={styles.taskCountUpdated}>
            <TaskCount
              container={container}
              openTasksCount={undefined}
              triageTasksCount={undefined}
            />
            <p className={styles.lastUpdated}>
              Updated: {formatDistanceStrict(new Date(), parseISO(updatedAt))}{' '}
              ago
            </p>
          </div>
        </div>
        <div className={styles.cardFooter} onClick={e => e.stopPropagation()}>
          {!isGuest && (
            <span
              className={styles.websiteMenu}
              onClick={e => e.stopPropagation()}
            >
              <WebsiteMenu
                container={container}
                handleOpenWebsiteModal={handleOpenModal}
                disabled={false}
                id={id}
                projectId={project.id}
                apiKey={apiKey}
                handleGetCoverImage={getCoverImage}
                openLabelsModal={() => setShowLabelModal(true)}
                hasLabels={!!site.labels?.length}
                projectSiteLabelsAllowed={projectSiteLabelsAllowed}
              />
            </span>
          )}
          <ShareButton onClick={onShareButtonClick} />
          <ProxyCompatibilityChecker
            proxyUrl={proxyUrl}
            projectSiteId={id}
            projectSiteUrl={url}
            getTriggerElement={() => null}
            project={project}
            onSharingClick={onSharingClick}
            showProxyPreview={showProxyPreview}
            isGuest={isGuest}
            childRef={childRef}
            showTour
          />
          <AddWebsiteModal
            container={container}
            isWebsiteModalOpen={openEditModal}
            activeItem=""
            handleCloseWebsiteModal={handleCloseWebsiteModal}
            title={strings.editWebsiteUrl}
            projectSiteUrl={url}
            projectUrlUpdating
            projectSiteId={id}
            hideInstallInstructions={showProxyPreview}
          />
          {projectSiteLabelsAllowed && (
            <SiteLabels
              open={showLabelModal}
              setOpen={setShowLabelModal}
              site={site}
              allLabels={allLabels}
              projectId={project.id}
              canEditLabels={!isGuest}
              container={container}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectSite;
